import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
    LayoutDashboard,
    Flame,
    BadgeCheck,
    ClipboardList,
    Map as MapIcon,
    AlertTriangle,
    BarChart3,
    Users,
    Building2,
    Bell,
    LogOut,
    ExternalLink,
    Shield,
    X,
    CheckCircle,
    Info,
    AlertCircle,
    BookOpen,
    Home,
    Activity,
    Wifi,
    Radio,
    MessageCircle,
    CreditCard,
    QrCode,
    Navigation,
    MapPinned,
    CalendarClock,
    Siren,
    FileLock2,
    BellRing
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/api';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useModules } from '@/hooks/useModules';
import { MODULE_BY_ROUTE, moduleForPath } from '@/services/modulesService';

interface Notification {
    id: number;
    title: string;
    message: string;
    type: string;
    severity: string;
    createdAt: string;
    status: string;
}

interface NavItem {
    name: string;
    href: string;
    icon: any;
    external?: boolean;
}

export default function Layout() {
    const location = useLocation();
    const { t } = useTranslation();
    const { user, logout } = useAuth();
    // Módulos contratados por la organización: el menú solo muestra lo incluido en su plan.
    const modules = useModules(!!user);
    const included = (href: string) => {
        const m = MODULE_BY_ROUTE[href];
        return !m || modules === null || modules.has(m);
    };
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    const notificationRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (showNotifications) {
            loadNotifications();
        }
    }, [showNotifications]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
                setShowNotifications(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadNotifications = async () => {
        try {
            setLoadingNotifications(true);
            const response = await api.get('/api/v1/alerts');
            setNotifications(response.data || []);
        } catch (error) {
            console.error('Error loading notifications');
            // Demo notifications if API fails
            setNotifications([
                { id: 1, title: 'Conflicto Detectado', message: 'Superposición en Sector Centro', type: 'CONFLICT', severity: 'HIGH', status: 'PENDING', createdAt: new Date().toISOString() },
                { id: 2, title: 'Sistema Operativo', message: 'Sincronización activa sin errores', type: 'SYSTEM', severity: 'INFO', status: 'PENDING', createdAt: new Date().toISOString() }
            ]);
        } finally {
            setLoadingNotifications(false);
        }
    };

    const markAsRead = async (id: number) => {
        try {
            await api.put(`/api/v1/alerts/${id}/acknowledge`);
            setNotifications(prev => prev.filter(n => n.id !== id));
        } catch (error) {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }
    };

    const canManageOperations = user?.roles?.some(r => ['ADMINISTRADOR', 'COORDINADOR', 'SUPERVISOR'].includes(r));
    const canEnroll = user?.roles?.some(r => ['ADMINISTRADOR', 'COORDINADOR'].includes(r));

    const navigation = [
        { name: t('nav.home'), href: '/welcome', icon: Home },
        { name: t('nav.dashboard'), href: '/dashboard', icon: LayoutDashboard },
        { name: t('nav.operations'), href: '/operations', icon: ClipboardList },
        { name: t('nav.tacticalMap'), href: '/map', icon: MapIcon },
        ...(canManageOperations ? [{ name: t('nav.geofences'), href: '/geofences', icon: Shield }] : []),
        ...(canManageOperations ? [{ name: t('nav.dispatch'), href: '/dispatch', icon: Navigation }] : []),
        { name: t('nav.emergencies'), href: '/emergencies', icon: Flame },
        { name: t('nav.credentials'), href: '/credentials', icon: BadgeCheck },
        { name: t('nav.patrol'), href: '/patrol', icon: MapPinned },
        { name: t('nav.scheduling'), href: '/scheduling', icon: CalendarClock },
        { name: t('nav.safety'), href: '/safety', icon: Siren },
        { name: t('nav.notifications'), href: '/notifications', icon: BellRing },
        ...(canManageOperations ? [{ name: t('nav.evidence'), href: '/evidence', icon: FileLock2 }] : []),
        { name: t('nav.criticalNodes'), href: '/critical-nodes', icon: AlertTriangle },
        { name: t('nav.reports'), href: '/reports', icon: BarChart3 },
        ...(canManageOperations ? [{ name: t('nav.opcEvaluation'), href: '/evaluation', icon: CheckCircle }] : []),
        { name: t('nav.manuals'), href: '/manuals', icon: BookOpen },
        { name: t('nav.liveCenter'), href: '/live', icon: Radio },
        ...(canManageOperations ? [{ name: t('nav.signalComms'), href: '/signal', icon: MessageCircle }] : []),
        ...(canEnroll ? [{ name: t('nav.qrEnrollment'), href: '/admin/enrollment', icon: QrCode }] : []),
    ].filter((item) => included(item.href));

    const adminNavigation: NavItem[] = [
        { name: t('nav.users'), href: '/admin/users', icon: Users },
        { name: t('nav.institutions'), href: '/admin/agencies', icon: Building2 },
        { name: t('nav.alerts'), href: '/admin/alerts', icon: Bell },
        { name: t('nav.devices'), href: '/admin/devices', icon: Wifi },
        { name: t('nav.adminTools'), href: '/admin/tools', icon: Activity },
        { name: t('nav.subscription'), href: '/billing', icon: CreditCard },
    ].filter((item) => included(item.href));

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    return (
        <div className="min-h-screen bg-[#050608] selection:bg-blue-500/30 font-sans text-gray-300">
            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/60 z-30 lg:hidden backdrop-blur-sm transition-opacity"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`fixed inset-y-0 left-0 w-72 bg-gray-900/40 backdrop-blur-3xl border-r border-white/5 z-40 shadow-[4px_0_24px_rgba(0,0,0,0.5)] flex flex-col transition-transform duration-300 ease-in-out lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                {/* Logo */}
                <div className="h-24 flex items-center px-8 border-b border-white/5 justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center p-2.5 shadow-lg shadow-blue-600/20">
                            <Shield className="w-full h-full text-white" />
                        </div>
                        <div>
                            <span className="text-2xl font-black text-white tracking-tighter">VANTAK</span>
                            <div className="text-[8px] text-gray-500 font-black uppercase tracking-[0.1em] leading-tight max-w-[140px]">{t('nav.platformTagline')}</div>
                        </div>
                    </div>
                    {/* Close button for mobile */}
                    <button
                        onClick={() => setIsSidebarOpen(false)}
                        className="lg:hidden p-2 text-gray-400 hover:text-white bg-white/5 rounded-lg"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Main Navigation */}
                <nav className="flex-1 overflow-y-auto p-6 space-y-1.5 custom-scrollbar">
                    {navigation.map((item) => {
                        const isActive = location.pathname === item.href;
                        const Icon = item.icon;
                        return (
                            <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setIsSidebarOpen(false)}
                                className={`flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-300 group ${isActive
                                    ? 'bg-blue-600/10 text-blue-400 border border-blue-500/20'
                                    : 'text-gray-500 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                <Icon className={`w-5 h-5 transition-transform group-hover:scale-110 ${isActive ? 'text-blue-500' : ''}`} />
                                <span className="text-sm font-black uppercase tracking-widest leading-none">{item.name}</span>
                                {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_#3b82f6]" />}
                            </Link>
                        );
                    })}

                    {/* Admin Section Separator - Only for Admins */}
                    {user?.roles?.includes('ADMINISTRADOR') && (
                        <div className="pt-8 pb-4">
                            <div className="px-5 text-[9px] font-black text-gray-600 uppercase tracking-[0.4em] mb-4">{t('nav.administrationSection')}</div>
                            <div className="space-y-1.5">
                                {adminNavigation.map((item) => {
                                    const isActive = location.pathname === item.href;
                                    const Icon = item.icon;
                                    if (item.external) {
                                        return (
                                            <a
                                                key={item.name}
                                                href={item.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-4 px-4 py-3.5 rounded-2xl text-gray-500 hover:text-white hover:bg-white/5 transition-all group"
                                                onClick={() => setIsSidebarOpen(false)}
                                            >
                                                <Icon className="w-5 h-5 group-hover:scale-110" />
                                                <span className="text-sm font-black uppercase tracking-widest leading-none">{item.name}</span>
                                                <ExternalLink className="ml-auto w-3 h-3 opacity-20" />
                                            </a>
                                        );
                                    }
                                    return (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            onClick={() => setIsSidebarOpen(false)}
                                            className={`flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-300 group ${isActive
                                                ? 'bg-purple-600/10 text-purple-400 border border-purple-500/20'
                                                : 'text-gray-500 hover:text-white hover:bg-white/5'
                                                }`}
                                        >
                                            <Icon className={`w-5 h-5 transition-transform group-hover:scale-110 ${isActive ? 'text-purple-500' : ''}`} />
                                            <span className="text-sm font-black uppercase tracking-widest leading-none">{item.name}</span>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </nav>

                {/* User Context & Logout */}
                <div className="p-6 border-t border-white/5">
                    <div className="bg-white/[0.03] rounded-3xl p-4 flex items-center gap-4 border border-white/5">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center border border-white/10 shrink-0">
                            <span className="text-lg font-black text-white uppercase">
                                {user?.fullName?.charAt(0) || 'U'}
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-black text-white truncate uppercase tracking-tighter">{user?.fullName || t('nav.defaultOperator')}</div>
                            <div className="text-[10px] text-gray-500 font-bold truncate uppercase tracking-widest">{user?.agency?.name || t('nav.defaultCommand')}</div>
                        </div>
                        <button
                            onClick={logout}
                            className="p-3 text-gray-500 hover:text-red-500 transition-colors bg-white/5 rounded-xl hover:bg-red-500/10"
                            title={t('nav.logout')}
                        >
                            <LogOut className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content Viewport */}
            <main className="lg:pl-72 flex flex-col min-h-screen transition-all duration-300">
                {/* Global Status Bar */}
                <header className="h-16 bg-gray-900/10 backdrop-blur-md border-b border-white/5 flex items-center justify-between px-4 lg:px-10 sticky top-0 z-20">
                    <div className="flex items-center gap-4">
                        {/* Hamburger Menu for Mobile */}
                        <button
                            onClick={() => setIsSidebarOpen(true)}
                            className="lg:hidden p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>

                        <div className="hidden sm:flex items-center gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_#22c55e]" />
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">{t('nav.syncActive')}</span>
                        </div>
                        {/* Mobile simplified title */}
                        <div className="sm:hidden flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                            <span className="text-xs font-black text-white tracking-widest">VANTAK</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 lg:gap-8">
                        <LanguageSwitcher />
                        <div className="relative" ref={notificationRef}>
                            <button
                                onClick={() => setShowNotifications(!showNotifications)}
                                className={`relative group p-2 rounded-xl transition-all ${showNotifications ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}
                            >
                                <Bell className="w-5 h-5 transition-transform group-hover:scale-110" />
                                {notifications.length > 0 && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-[#050608] shadow-sm animate-bounce" />}
                            </button>

                            {/* Notifications Dropdown */}
                            {showNotifications && (
                                <div className="absolute right-0 mt-4 w-80 sm:w-96 bg-gray-900/90 backdrop-blur-3xl border border-white/10 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                                    <div className="p-6 border-b border-white/5 flex items-center justify-between">
                                        <h3 className="text-xs font-black text-white uppercase tracking-[0.2em]">{t('nav.notifications')}</h3>
                                        <span className="px-2 py-1 bg-red-500/10 text-red-500 rounded-lg text-[10px] font-black uppercase">{t('nav.newCount', { count: notifications.length })}</span>
                                    </div>
                                    <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                                        {loadingNotifications ? (
                                            <div className="p-10 flex flex-col items-center gap-4 opacity-50">
                                                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                                <span className="text-[10px] font-black uppercase tracking-widest">{t('nav.syncing')}</span>
                                            </div>
                                        ) : notifications.length === 0 ? (
                                            <div className="p-10 text-center flex flex-col items-center gap-4 opacity-20">
                                                <CheckCircle className="w-10 h-10" />
                                                <span className="text-[10px] font-black uppercase tracking-widest">{t('nav.noPendingAlerts')}</span>
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-white/5">
                                                {notifications.map((n) => (
                                                    <div key={n.id} className="p-5 hover:bg-white/[0.03] transition-colors group relative">
                                                        <div className="flex gap-4">
                                                            <div className="mt-1 shrink-0">
                                                                {n.severity === 'HIGH' ? (
                                                                    <AlertCircle className="w-5 h-5 text-red-500" />
                                                                ) : (
                                                                    <Info className="w-5 h-5 text-blue-500" />
                                                                )}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-[10px] font-black text-white uppercase tracking-wider mb-1 flex items-center justify-between">
                                                                    {n.title}
                                                                    <span className="text-[8px] text-gray-500 font-bold">{new Date(n.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                                </div>
                                                                <p className="text-xs text-gray-400 leading-relaxed line-clamp-2">{n.message}</p>
                                                            </div>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }}
                                                                className="opacity-0 group-hover:opacity-100 p-2 hover:text-white text-gray-500 transition-all bg-white/5 rounded-lg"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4 bg-white/[0.02] border-t border-white/5">
                                        <Link
                                            to="/admin/alerts"
                                            onClick={() => setShowNotifications(false)}
                                            className="flex items-center justify-center py-3 w-full bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg shadow-blue-600/20"
                                        >
                                            {t('nav.viewAlertHistory')}
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="text-[10px] font-mono text-gray-500 font-bold flex items-center gap-3">
                            <span className="hidden sm:inline opacity-40">UTC-3</span>
                            <span className="text-gray-300 bg-white/5 px-3 py-1 rounded-lg">
                                {new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                        </div>
                    </div>
                </header>

                {/* Sub-Route Page Injection */}
                <div className="flex-1 p-4 lg:p-10 overflow-y-auto custom-scrollbar">
                    {(() => {
                        const required = moduleForPath(location.pathname);
                        return required && modules !== null && !modules.has(required) ? (
                            <div role="alert" className="mx-auto mt-16 max-w-lg rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
                                <h2 className="text-lg font-semibold text-white">{t('modules.notIncludedTitle')}</h2>
                                <p className="mt-2 text-sm text-gray-400">{t('modules.notIncludedBody')}</p>
                            </div>
                        ) : <Outlet />;
                    })()}
                </div>
            </main>
        </div>
    );
}
